import { BACKEND_URL } from "../config";
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API = `${BACKEND_URL}/api`;

function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking'); // 'checking', 'success', 'failed'
  const [message, setMessage] = useState('Verifying your payment...');
  const [pollAttempts, setPollAttempts] = useState(0);
  const maxPollAttempts = 5;

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      setMessage('Invalid payment session');
      return;
    }

    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${API}/payments/checkout-status/${sessionId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        if (response.data.payment_status === 'paid' && response.data.subscription_upgraded) {
          setStatus('success');
          setMessage('Payment successful! You are now a Tier 1 subscriber.');
          
          // Redirect to calculator after 3 seconds
          setTimeout(() => {
            navigate('/calculator');
          }, 3000);
          return;
        }
        
        // Payment still pending, poll again
        if (pollAttempts < maxPollAttempts) {
          setPollAttempts(prev => prev + 1);
          setTimeout(() => pollPaymentStatus(), 2000);
        } else {
          setStatus('failed');
          setMessage('Payment verification timed out. Please check your email for confirmation.');
        }
      }
    } catch (error) {
      setStatus('failed');
      setMessage(error.response?.data?.detail || 'Failed to verify payment status');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      fontFamily: "'Inter', sans-serif"
    }}>
      <div style={{
        width: '100%',
        maxWidth: '500px',
        background: 'white',
        borderRadius: '20px',
        padding: '3rem',
        boxShadow: '0 10px 40px rgba(139, 92, 246, 0.2)',
        border: '2px solid #e9d5ff',
        textAlign: 'center'
      }}>
        {/* Status Icon */}
        <div style={{ fontSize: '4rem', marginBottom: '1.5rem' }}>
          {status === 'checking' && '⏳'}
          {status === 'success' && '✅'}
          {status === 'failed' && '❌'}
        </div>

        {/* Status Message */}
        <h2 style={{
          fontSize: '1.75rem',
          fontWeight: '700',
          color: status === 'success' ? '#16a34a' : status === 'failed' ? '#dc2626' : '#7c3aed',
          marginBottom: '1rem'
        }}>
          {status === 'checking' && 'Verifying Payment...'}
          {status === 'success' && 'Payment Successful!'}
          {status === 'failed' && 'Payment Issue'}
        </h2>

        <p style={{
          color: '#64748b',
          fontSize: '1rem',
          lineHeight: '1.6',
          marginBottom: '2rem'
        }}>
          {message}
        </p>

        {/* Loading indicator */}
        {status === 'checking' && (
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e9d5ff',
            borderTop: '4px solid #7c3aed',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 2rem'
          }} />
        )}

        {/* Success Info */}
        {status === 'success' && (
          <div style={{
            padding: '1.5rem',
            background: '#d1fae5',
            borderRadius: '12px',
            marginBottom: '2rem'
          }}>
            <div style={{ color: '#065f46', fontWeight: '600', marginBottom: '0.5rem' }}>
              🎉 Welcome to Tier 1!
            </div>
            <div style={{ color: '#047857', fontSize: '0.875rem' }}>
              You now have unlimited access to all features including MT5 integration.
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <button
          onClick={() => navigate('/calculator')}
          style={{
            width: '100%',
            padding: '1rem',
            background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '700',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
        >
          {status === 'success' ? 'Go to Calculator →' : 'Back to Dashboard'}
        </button>

        {status === 'checking' && (
          <div style={{
            marginTop: '1rem',
            fontSize: '0.75rem',
            color: '#94a3b8'
          }}>
            Attempt {pollAttempts + 1} of {maxPollAttempts}
          </div>
        )}
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default PaymentSuccess;
